import { createRouter, createWebHistory } from 'vue-router';
import MainRoutes from './MainRoutes';
import AuthRoutes from './AuthRoutes';
import { useAuthStore } from '@/stores/auth';
import { store, key } from '@/storeVuex';
import { SecurityValidator } from '@/utils/security';
import { useCustomizerStore } from '@/stores/customizer';
import { useFeatureFlagsStore } from '@/stores/featureFlags';
import { usePlanAccessStore } from '@/stores/planAccess';
import { isRestrictedSubscription } from '@/utils/subscriptionAccess';
import { fetchSubscriptionData, resetSubscriptionData } from '@/utils/subscriptionData';

import { isFullAdmin } from '@/utils/fullAdmin';
import { hasPermission } from '@/utils/permissions';
import { isChunkLoadError, reloadForChunkError } from '@/utils/chunkErrors';
const VITE_APP_NAME = `${import.meta.env.VITE_APP_NAME}`;

// --- Subscription access restriction ---
// Any payment-failed account is restricted: past_due (still retrying) as well
// as unpaid / expired / canceled (max retries reached). When restricted, the
// user may only reach Login and Plan & Billings (/subscription-settings); every
// other route redirects there.
// Keyed by user id, not a bare flag. The social callback can replace the
// signed-in user without an intervening logout, so a flag that only means
// "checked" or "not checked" cannot tell a fresh account from the one it was
// last computed for. An unrestricted user A's cached "false" would otherwise
// be handed straight to a restricted user B who signs in as the next thing
// that happens in the same tab, bypassing the payment gate entirely.
let subRestrictionCheckedForUserId: any = null;
let subRestricted = false;
let subRestrictionLastFailedAt = 0;

function resetSubscriptionRestriction() {
    subRestrictionCheckedForUserId = null;
    subRestricted = false;
    subRestrictionLastFailedAt = 0;
    // The shared response cache is per-user and resets itself on an id change,
    // but a sign-out followed by the SAME id signing back in is not an id
    // change. Clear it here too, where the gate's own cache is cleared.
    resetSubscriptionData();
}

async function isSubscriptionRestricted(userId: any): Promise<boolean> {
    if (!userId) return false;
    // Once confirmed NOT restricted for THIS user, trust it for the session.
    // A different id always re-fetches, whether or not the previous one had
    // been cleared -- that is what makes the cache safe to keep across a
    // social sign-in that switches accounts mid-session.
    if (subRestrictionCheckedForUserId === userId && !subRestricted) return false;
    // A timeout/error means the answer is unknown, not "not restricted" --
    // caching it as the latter (by stamping subRestrictionCheckedForUserId
    // below regardless of outcome) let one slow response permanently exempt
    // a past_due/unpaid/canceled account from the payment gate for the rest
    // of the tab session. Retry shortly instead of both extremes: not an
    // unbounded re-race on every navigation while the endpoint is down, and
    // not a session-long cache of an answer that was never actually obtained.
    if (Date.now() - subRestrictionLastFailedAt < 10000) return false;
    try {
        // Bounded for the same reason the social callback bounds its own
        // waits: this runs in the router guard, on the path a fresh social
        // login takes straight after committing its session, and an
        // unbounded request here leaves an authenticated user stuck on
        // "Signing you in..." for as long as the endpoint takes to answer.
        //
        // maxAge 0: the gate never acts on a cached answer. The only navigations
        // that reach this line are the first one for a user and every one after
        // that for an account already found restricted, and the latter must see
        // a payment the moment it lands, not up to a TTL later. The request is
        // still shared — fetchSubscriptionData joins whatever is in flight, and
        // its answer is what the profile menu and planAccess store then read
        // instead of each issuing their own (Sentry BS-WEB-APP-16P).
        //
        // The timeout bounds this caller, not the request: the shared promise
        // stays alive and still resolves for the other two.
        const res: any = await Promise.race([
            fetchSubscriptionData(userId, { maxAge: 0 }),
            new Promise((_, reject) => setTimeout(() => reject(new Error('subscription check timed out')), 5000)),
        ]);
        subRestricted = isRestrictedSubscription(res);
        subRestrictionCheckedForUserId = userId;
    } catch {
        // Fail open — never lock users out because the status check errored
        // or timed out. Do NOT stamp subRestrictionCheckedForUserId here --
        // that's the cache for a real answer, and this isn't one.
        subRestricted = false;
        subRestrictionLastFailedAt = Date.now();
    }
    return subRestricted;
}

// --- Version detection ---
// Polls version.json every 5 seconds. Also checks when the user returns to the tab or window.
let currentBuildHash: string | null = null;
let versionDetected = false;
let versionPollTimer: ReturnType<typeof setInterval> | null = null;

async function checkForNewVersion() {
    if (versionDetected) return; // Already shown the modal, stop checking
    try {
        const res = await fetch('/version.json?t=' + Date.now(), { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();

        if (!currentBuildHash) {
            currentBuildHash = data.buildHash;
            return;
        }

        if (data.buildHash !== currentBuildHash) {
            console.log(`[version-check] New version detected: ${currentBuildHash} → ${data.buildHash}`);
            versionDetected = true;
            stopVersionPolling();
            window.dispatchEvent(new CustomEvent('app-version-updated'));
        }
    } catch {
        // Silently ignore — version.json may not exist in dev
    }
}

function startVersionPolling() {
    if (versionPollTimer) return;
    versionPollTimer = setInterval(checkForNewVersion, 30000);
}

function stopVersionPolling() {
    if (versionPollTimer) {
        clearInterval(versionPollTimer);
        versionPollTimer = null;
    }
}

// Check immediately when user returns to the tab or window
function handleVisibilityChange() {
    if (document.visibilityState === 'visible') {
        checkForNewVersion();
    }
}
function handleWindowFocus() {
    checkForNewVersion();
}

export const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes: [
        {
            path: '/:pathMatch(.*)*',
            component: () => import('@/views/pages/Authentication/Error.vue')
        },
        // PDF Render route (no auth required - accessed by Playwright)
        {
            path: '/proposal-render/:jobId',
            name: 'proposal-render',
            component: () => import('@/views/pages/ClientPortal/ProposalRender.vue'),
            meta: { requiresAuth: false }
        },
        // Public Tender View route (no auth required - accessed by clients via link)
        {
            path: '/tender/view/:token',
            name: 'public-tender-view',
            component: () => import('@/views/pages/PublicTender/TenderView.vue'),
            meta: { requiresAuth: false, title: 'View Document' }
        },
        // Public Variation sign route (Epic 3 — client signs a change order via link)
        {
            path: '/variation/view/:token',
            name: 'public-variation-view',
            component: () => import('@/views/pages/PublicVariation/VariationView.vue'),
            meta: { requiresAuth: false, title: 'Variation' }
        },
        // Public RFQ quote submission (no auth — suppliers open it from the RFQ
        // email; token is per-recipient, so responses self-attribute)
        {
            path: '/quote/:token',
            name: 'public-rfq-quote',
            component: () => import('@/views/pages/PublicRfq/RfqQuoteSubmit.vue'),
            meta: { requiresAuth: false, title: 'Submit Your Quote' }
        },
        MainRoutes,
        AuthRoutes
    ]
});

// Capture the current build hash, then start polling + listening for tab focus
checkForNewVersion();
startVersionPolling();
document.addEventListener('visibilitychange', handleVisibilityChange);
window.addEventListener('focus', handleWindowFocus);

// Handle Vite chunk loading errors after deployment
// When new assets are deployed with different hashes, stale chunks fail to load.
// This reloads the page once to fetch the new chunks, using sessionStorage to prevent loops.
//
// The wordings live in isChunkLoadError() because each engine phrases the same
// rejection differently -- this used to match only the Chromium and Safari
// strings, so a Firefox user whose tab had outlived a deploy got the "page
// could not be found" screen instead of a reload, on any lazily-loaded page.
router.onError((error, to) => {
    if (!isChunkLoadError(error)) return;
    if (reloadForChunkError(to.fullPath)) return;
    // Already reloaded recently — don't loop, let the error page show.
    console.error('Chunk load failed after reload attempt:', error);
});

// Vite reports a failed chunk preload here rather than through the router when
// the import is kicked off by a prefetch rather than by a navigation. Same
// cause, same single-reload recovery, same cooldown.
//
// preventDefault() is what stops Vite rethrowing — without it the rejection
// escapes as an unhandled error naming the asset and nothing else, which is
// what BS-WEB-APP-168 ("Unable to preload CSS for /assets/index-C0tP6mEE.css")
// was, filed from a build that predates this listener.
window.addEventListener('vite:preloadError', (event: any) => {
    event?.preventDefault?.();
    if (reloadForChunkError(window.location.pathname + window.location.search)) return;

    // Cooldown: a reload already happened and the asset is STILL missing, so
    // reloading again would spin. But preventDefault() has told Vite to carry
    // on without the dep, and for a CSS dep that means the module loads and
    // the page renders with no stylesheet. Silence is the one answer this path
    // must not give — say it, the way router.onError already does for the
    // navigation case.
    console.error('Chunk preload failed after reload attempt:', event?.payload ?? event);
});

/**
 * Routes that are permissionable pages but must never be gated.
 *
 * Search runs over records the user can already see and returns nothing they
 * could not otherwise reach, so gating it behind a permission the matrix does
 * not define would break search for every non-admin.
 *
 * The August 2026 matrix DOES define it — "Global search" (`global_search`,
 * alias `SearchResults`), View for all four roles — so this entry is now a
 * transitional escape hatch rather than a permanent one. Retire it only AFTER
 * the updated templates have been applied to every company: until a role
 * actually carries `SearchResults__View`, removing this bounces every
 * non-admin off /search.
 */
const UNGATED_PAGES = new Set(['SearchResults']);

/**
 * Route name -> the catalogue slug that actually governs it.
 *
 * Route names ARE permission slugs almost everywhere, which is why
 * isPermittedPage() can simply append "__View". `/estimate-list` is the
 * exception: its route is named `estimate_list`, nothing writes an
 * `estimate_list__View` key, and the screen belongs to the `estimates` row of
 * the matrix — the catalogue lists its tabs as "/estimate-list (Overhead)",
 * "/estimate-list (Costings)" and so on, all under Estimating.
 *
 * It was `isPage: false`, so it had NO route gate at all: the estimate DETAIL
 * screen — costings, overheads and margins, takeoff, specifications, quotes and
 * contract, six catalogue rows and the commercially sensitive numbers — opened
 * for anyone who could log in and type the URL, while the estimate LIST in
 * front of it was gated correctly. Marking it a page without this map would
 * instead bounce EVERYONE but a full admin, because the derived key does not
 * exist on any role.
 *
 * Prefer adding an alias in bsapi's permission_catalogue.php to growing this
 * map. It is here because an alias only reaches a role when that role is
 * re-synced from its template, and the roles that predate the catalogue have
 * not been.
 */
const PAGE_PERMISSION_ALIASES: Record<string, string> = {
    estimate_list: 'estimates',
    // /smart-quote-detail is the same screen as /smart-quote, whose route name
    // `client_portal` IS a catalogue alias of smart_quote_proposals. The detail
    // route's name is not, so it is mapped here rather than left ungated.
    client_portal_detail: 'smart_quote_proposals',
    // /account-settings is a user's OWN profile. Its route is named
    // `account_settings`, which is not a catalogue key, so the derived
    // `account_settings__View` is absent from every role and the guard has been
    // bouncing every non-admin off their own profile page. The catalogue row is
    // `own_profile`, which the matrix templates do write.
    account_settings: 'own_profile',
    // The seven Help Centre authoring routes. All were isPage:false, so anyone
    // who could log in could create, edit or delete help articles, guides and
    // tours by typing the URL -- `help_center_admin` was read nowhere at all.
    help_admin: 'help_center_admin',
    help_admin_article_create: 'help_center_admin',
    help_admin_article_edit: 'help_center_admin',
    help_admin_guide_create: 'help_center_admin',
    help_admin_guide_edit: 'help_center_admin',
    help_admin_tour_create: 'help_center_admin',
    help_admin_tour_edit: 'help_center_admin'
};

/**
 * Route names of every `isPage: true` route, read from the RAW route
 * definitions.
 *
 * `isPage` is a custom top-level property on the route object, and Vue Router
 * does not carry custom top-level properties onto the normalised records in
 * `to.matched` (only `meta` survives). Reading it off `to.matched` would
 * silently evaluate to undefined and gate nothing at all, so the set is built
 * once from MainRoutes.children instead.
 */
const PERMISSIONED_PAGES: Set<string> = new Set(
    ((MainRoutes as any).children || [])
        .filter((r: any) => r.isPage === true && r.name)
        .map((r: any) => String(r.name).split('?')[0])
);

/**
 * Does this user hold "<route name>__View" for the page being opened?
 *
 * Route names ARE the permission slugs — the nav components build the same
 * "<name>__View" key. Names carrying a query template ("foo?job=:id") are
 * truncated at the "?" so they resolve to the same slug the menu uses.
 */
function isPermittedPage(to: any, user: any): boolean {
    const slug = String(to.name ?? '').split('?')[0];

    // Only routes explicitly marked as pages are permissionable.
    if (!slug || !PERMISSIONED_PAGES.has(slug)) return true;

    if (isFullAdmin(user)) return true;
    if (UNGATED_PAGES.has(slug)) return true;

    return hasPermission(user, PAGE_PERMISSION_ALIASES[slug] ?? slug, 'View');
}

router.beforeEach(async (to, from, next) => {
    if (to.meta.title) {
        document.title = VITE_APP_NAME + ' - ' + to.meta.title;
    }

    // Always clear preloader on any navigation to prevent logo flash between pages
    const customizer = useCustomizerStore();
    customizer.preloader = false;

    const authPages = ['/login', '/two-step'];
    const isAuthPage = authPages.includes(to.path);

    // The provider lands the browser on /auth/social BEFORE there is a session,
    // carrying a one-time code the view exchanges immediately.
    //
    // It needs SOME of what an auth page gets and not all of it, which is why
    // it is tracked separately rather than added to the list above:
    //
    //  - exempt from the session-timeout redirect, or anyone whose previous
    //    session had aged out is logged out and sent to /login, throwing away
    //    the code and the sign-in they just completed at the provider
    //  - allowed while subscription-restricted, so they can finish signing in
    //    and meet the restriction on the next navigation instead
    //  - but NOT subject to the "already logged in, go to the dashboard" rule.
    //    Listing it in authPages did exactly that: anyone who still held a
    //    session was redirected away before the exchange could run and the code
    //    was silently discarded, so signing in as a different account left them
    //    sitting in the old one.
    const isSocialCallback = to.path === '/auth/social';

    const user: any = store.state.user;

    // No active session → clear any cached restriction so the next login re-checks.
    if (!user || !user.user) {
        resetSubscriptionRestriction();
    }

    // Sync Pinia auth store with Vuex store before route navigation
    try {
        const authStore = useAuthStore();
        authStore.syncWithVuexStore();

        // Check for session timeout
        // isSocialCallback goes FIRST. checkSessionTimeout() has a side effect --
        // it calls logout() on an expired session -- and && evaluates left to
        // right, so putting it first ran that logout on every social callback
        // whose OLD session had timed out, regardless of the flag after it. The
        // exchange in progress could then be cleared out from under itself.
        if (!isSocialCallback && !authStore.checkSessionTimeout() && !isAuthPage) {
            SecurityValidator.logSecurityEvent('session_timeout_redirect', {
                from: from.path,
                to: to.path
            });
            return next({ name: 'login' });
        }
    } catch (error) {
        // If auth store check fails, continue with existing logic
        console.error('Auth store sync failed during navigation:', error);
    }

    // Log navigation for security monitoring
    if (user && user.user) {
        SecurityValidator.logSecurityEvent('navigation', {
            from: from.path,
            to: to.path,
            username: user.username || 'unknown'
        });
    }

    let isLoggedIn = false;
    let isTwoFactor = false;
    let isAuth = false;

    if (to.matched.some((record) => record.meta.requiresAuth)) {
        isAuth = true;
        if (user) {
            if (user.hasAuthFactor && !user.isAuthFactorPass) {
                isTwoFactor = true;
                isLoggedIn = false;
            }
            if (user.user) {
                isTwoFactor = false;
                isLoggedIn = true;
            }
        }
    } else {
        isAuth = false;
    }

    if (isTwoFactor) {
        next({ name: 'two_factor' });
    } else if (isAuth && !isLoggedIn) {
        SecurityValidator.logSecurityEvent('unauthorized_access_attempt', {
            path: to.path,
            requiresAuth: true,
            isLoggedIn: false
        });
        next({ name: 'login' });
    } else {
        // The social callback skips this whole block, not just the timeout
        // exemption. isSubscriptionRestricted has no timeout of its own, and it
        // was still being awaited for the OLD account on the way to /auth/social
        // -- a slow or hanging subscription endpoint stalled the navigation
        // before SocialCallback could even mount, and the 60-second exchange
        // code expired while the browser sat on the previous screen. The new
        // account's restriction is checked correctly on the NEXT navigation,
        // once SocialCallback redirects after a successful exchange.
        if (user && user.user && !isSocialCallback) {
            const restricted = await isSubscriptionRestricted(user.user?.id);

            // While restricted, only the auth pages, logout, and the Plan &
            // Billings page are reachable — anything else bounces to billing.
            const allowedWhenRestricted =
                isAuthPage ||
                isSocialCallback ||
                to.name === 'subscription_settings' ||
                to.path === '/subscription-settings' ||
                to.name === 'logout';

            if (restricted && !allowedWhenRestricted) {
                return next({ name: 'subscription_settings' });
            }

            // A social_error on /login is the callback reporting why it could not
            // sign someone in -- rejected account, expired code, exchange failure.
            // All four of those happen while an OLD session can still exist (the
            // callback deliberately does not require a clean slate), and without
            // this exception every one of them was caught by the very next line
            // and silently redirected to the dashboard before LoginForm could
            // render the message it was sent to show.
            if (isAuthPage && !(to.path === '/login' && to.query.social_error)) {
                // Logged-in user hitting an auth page → billing if restricted,
                // otherwise the dashboard (preserves existing behavior).
                return next(restricted ? { name: 'subscription_settings' } : { name: 'dashboard' });
            }

            // Admin-only gate: routes flagged `requiresAdmin` are reachable only by
            // Full Admins (usertype 1) — the same rule NavCollapse.vue applies to
            // `isusertype: 1` menu items. Hiding the menu entry was never enough:
            // `requiresAuth` alone let ANY signed-in user open these by typing the
            // URL. Runs before the feature-flag gate so it can't be skipped by a
            // flag fetch error, and fails CLOSED — a missing/NaN usertype is denied.
            const needsAdmin = to.matched.some((r: any) => r.meta?.requiresAdmin);
            if (needsAdmin && Number(user.user?.usertype) !== 1) {
                SecurityValidator.logSecurityEvent('unauthorized_access_attempt', {
                    path: to.path,
                    requiresAdmin: true,
                    usertype: user.user?.usertype
                });
                return next({ name: 'dashboard' });
            }

            // Internal-staff gate, which is NOT the same thing as the one above.
            // `requiresAdmin` means "this company's Full Admin" (usertype 1) --
            // a customer. A page that reads BuiltSimple's own cross-tenant data
            // needs OUR staff, who are identified by portal === 'admin', and who
            // LoginForm/SocialCallback refuse at the door of this app. So this
            // gate is closed to everyone here, deliberately: the page's data
            // source sits behind Laravel's `admin.portal` middleware and would
            // reject a webapp token regardless. Without it, At-Risk Trials read
            // as reachable to every customer Full Admin and failed with an
            // unexplained error instead of never opening.
            const needsInternalStaff = to.matched.some((r: any) => r.meta?.requiresInternalStaff);
            if (needsInternalStaff && user.user?.portal !== 'admin') {
                SecurityValidator.logSecurityEvent('unauthorized_access_attempt', {
                    path: to.path,
                    requiresInternalStaff: true,
                    portal: user.user?.portal
                });
                return next({ name: 'dashboard' });
            }

            // Permission gate: a route marked `isPage: true` is a permissionable
            // page, and the user needs "<route name>__View" in their role to
            // open it. The nav components have always hidden these menu items,
            // but hiding a link is not access control — until this gate existed,
            // typing the URL opened any page regardless of role.
            //
            // Same bypass the nav uses (Full Admin sees everything) and the same
            // fail-closed rule (a slug the role does not mention is denied), so
            // a user's reachable pages exactly match their visible menu.
            if (!isPermittedPage(to, user.user)) {
                SecurityValidator.logSecurityEvent('unauthorized_access_attempt', {
                    path: to.path,
                    routeName: String(to.name ?? ''),
                    reason: 'missing_page_permission'
                });
                return next({ name: 'dashboard' });
            }

            // Runtime feature-flag gate: if this route is flagged hidden for the
            // current environment, block direct URL access and bounce to the
            // dashboard. Fails OPEN — a flag fetch error never blocks navigation.
            try {
                const featureFlags = useFeatureFlagsStore();
                // Load both the env flags and the account's admin feature
                // toggles (Mat AI / Smart Quote) — planAccess.load() feeds the
                // latter into featureFlags.userHidden — so a direct URL to a
                // feature the account hasn't been granted is reliably blocked.
                //
                // Bounded, not merely caught. planAccess.load() memoizes its
                // in-flight request on `this.loading` and hands that SAME
                // promise to every caller until it settles. The social
                // callback already bounds its own wait on this exact promise
                // to 5s and moves on — but without the same bound here, the
                // very next navigation re-awaits that still-pending promise
                // with no limit, so the callback's timeout gets defeated one
                // guard later instead of actually protecting the user.
                await Promise.race([
                    Promise.all([
                        featureFlags.load(),
                        usePlanAccessStore().load(user.user?.id),
                    ]),
                    new Promise((resolve) => setTimeout(resolve, 5000)),
                ]);
                const featureKey = to.matched.map((r: any) => r.meta?.featureKey).find(Boolean);
                if (featureKey && featureFlags.isHidden(featureKey)) {
                    return next({ name: 'dashboard' });
                }
            } catch (e) {
                console.error('Feature-flag gate skipped:', e);
            }

            return next();
        }

        next();
    }
});
